import { toPercent, toDecPercent } from '@utils/Format'
import { resolveGroupLabel } from '@modules/consolidator/utils/resolve-group-label/resolve-group-label'
import { resolveAssetGroupKey } from '@modules/consolidator/utils/resolve-asset-group-key/resolve-asset-group-key'
import {
  getSuggestedOnlyClasses,
  isClassGrouped as isClassGroupedForSuggested,
} from '@modules/consolidator/utils/format-alocacao-estrategica-chart/format-alocacao-estrategica-chart'
import {
  isPosicaoZeroed,
  resolveAtivoPl,
  resolvePosicaoPlTotal,
} from '@modules/consolidator/utils/posicao-pl-fallback/posicao-pl-fallback'

export const formatAllocationStatistics = (walletAnalysis) => {
  if (!walletAnalysis) return {}
  const result = {}
  const portfolioStats = walletAnalysis.portfolio_statistics
  if (portfolioStats && typeof portfolioStats === 'object') {
    result['Todos'] = portfolioStats
  }
  const data = walletAnalysis.breakdown && walletAnalysis.breakdown.data
  if (data) {
    Object.entries(data).forEach(([name, group]) => {
      if (group && group.statistics) {
        result[name] = group.statistics
      }
    })
  }
  return result
}

export const formatAllocationSuitability = (walletAnalysis, params = {}) => {
  const expanded = !!params.fundoCompostoExpandido
  const suitability = (walletAnalysis && walletAnalysis.suitability) || {}
  const statusKey = expanded
    ? 'client_allocation_status_expanded'
    : 'client_allocation_status'
  const validKey = expanded
    ? '_is_valid_client_allocation_expanded'
    : '_is_valid_client_allocation'
  return {
    perfil: suitability.perfil || '',
    statusBalanceamento: suitability[statusKey] || '',
    alocacaoClienteInvalida: suitability[validKey] === false,
  }
}

export const formatAllocationVol = ({
  classe,
  benchSelected,
  withBench = true,
  statistics,
  benchmarks,
  mapperBenchmarks,
}) => {
  const stats = statistics || {}
  if (!stats.Todos || !stats[classe]) return '-'

  const groupBench = stats[classe].benchmarks_statistics || {}
  if (benchSelected && !groupBench[benchSelected.id_benchmark]) return '-'

  const allBenchmarks = Array.isArray(benchmarks) ? benchmarks : []
  const { bench, benchName } = resolveBenchInfo({
    benchSelected,
    mapper: mapperBenchmarks || {},
    classe,
    allBenchmarks,
  })

  const volFinal = computeVolDiff({
    classeStats: stats[classe],
    groupBench,
    bench,
  })

  const formattedDiff = toPercent(volFinal * 100)
  if (!withBench) return formattedDiff

  return formatBenchmarkLabel({
    name: benchName,
    diff: volFinal,
    formattedDiff,
  })
}

export const buildAllocationRentMap = ({
  statistics,
  benchmarks,
  mapperBenchmarks,
}) => {
  const stats = statistics || {}
  const allBenchmarks = Array.isArray(benchmarks) ? benchmarks : []
  const mapper = mapperBenchmarks || {}
  const cdiBenchmark = findCdiBenchmark(allBenchmarks)

  return Object.entries(stats).reduce((acc, [key, next]) => {
    if (key === 'Todos' || !next) return acc

    const mapperEntry = mapper[key] || cdiBenchmark || {}
    const idBenchmark = mapperEntry.id_benchmark || mapperEntry.id
    const benchStats = (next.benchmarks_statistics || {})[idBenchmark] || {}
    const benchmark =
      allBenchmarks.find((b) => b && b.id === idBenchmark) || {}

    if (benchmark.nome === 'CDI') {
      if (!benchStats.rent_inicio) {
        acc[key] = '-'
        return acc
      }
      acc[key] =
        toPercent((next.inicio / benchStats.rent_inicio) * 100) + ' CDI'
      return acc
    }

    const diff = next.inicio - benchStats.rent_inicio
    acc[key] = formatBenchmarkLabel({
      name: benchmark.nome,
      diff,
      formattedDiff: toDecPercent(diff),
    })
    return acc
  }, {})
}

const CLASS_GROUPED_KEYS = new Set(['classe', null, undefined, ''])

export const buildAllocationRows = (walletAnalysis, getColor, params = {}) => {
  if (
    !walletAnalysis ||
    !walletAnalysis.breakdown ||
    !walletAnalysis.breakdown.data
  ) {
    return []
  }

  const expanded = !!params.fundoCompostoExpandido
  const groupBy = params.groupBy
  const data = walletAnalysis.breakdown.data
  const suitability = walletAnalysis.suitability || {}
  const isClassGrouped = CLASS_GROUPED_KEYS.has(groupBy)
  const useLookThrough =
    isClassGrouped && expanded && !!suitability.fracoes_expanded

  const entries = useLookThrough
    ? buildEntriesFromFracoes(suitability.fracoes_expanded)
    : buildEntriesFromBreakdown(
        data,
        pickAllocationFracoes(suitability, expanded),
        {
          byGroup: buildCotizedByGroup(walletAnalysis, groupBy),
          plTotal: resolvePosicaoPlTotal(walletAnalysis),
        },
        {
          walletAnalysis,
          expanded,
          appendSuggestedOnly: isClassGroupedForSuggested(groupBy),
        }
      )

  return [...entries]
    .sort((a, b) => b.value - a.value)
    .map(({ name, value, suitability: groupSuitability }, index) => ({
      name: resolveGroupLabel(name, params.groupLabelByKey) || name,
      value: toPercent(value, 1),
      color: getColor ? getColor(name, index) : '',
      diff: (groupSuitability && groupSuitability.diff) || 0,
      composicao_perfil:
        (groupSuitability && groupSuitability.composicao_perfil) || 0,
      signal: (groupSuitability && groupSuitability.signal) || '',
    }))
}

const buildEntriesFromFracoes = (fracoes) =>
  Object.entries(fracoes).map(([name, frac]) => ({
    name,
    value: frac && Number.isFinite(frac.fracao) ? frac.fracao * 100 : 0,
    suitability: frac,
  }))

const buildEntriesFromBreakdown = (data, fracoes, cotized = {}, ctx = {}) => {
  const cotizedTotals = cotized.byGroup
  const plTotal = cotized.plTotal
  const useCotized =
    !!cotizedTotals && Number.isFinite(plTotal) && plTotal !== 0
  const plFimTotal = Object.values(data).reduce(
    (sum, group) => sum + readPlFim(group),
    0
  )
  const breakdownEntries = Object.entries(data).map(([name, group]) => ({
    name,
    value: useCotized
      ? readCotizedPercent(name, cotizedTotals, plTotal)
      : readPlFimPercent(group, plFimTotal),
    suitability: fracoes[name] || (group && group.suitability) || null,
  }))
  if (!ctx.appendSuggestedOnly) return breakdownEntries
  const suggestedOnly = buildSuggestedOnlyEntries(
    ctx.walletAnalysis,
    fracoes,
    breakdownEntries.map((entry) => entry.name),
    ctx.expanded
  )
  return breakdownEntries.concat(suggestedOnly)
}

const buildSuggestedOnlyEntries = (
  walletAnalysis,
  fracoes,
  existingNames,
  expanded
) =>
  getSuggestedOnlyClasses(walletAnalysis, existingNames, !!expanded).map(
    ({ name }) => ({
      name,
      value: 0,
      suitability: (fracoes && fracoes[name]) || null,
    })
  )

const readPlFim = (group) => {
  if (!group) return 0
  const pl = group.pl_fim
  return Number.isFinite(pl) ? pl : 0
}

const readPlFimPercent = (group, plFimTotal) =>
  plFimTotal ? (readPlFim(group) / plFimTotal) * 100 : 0

const readCotizedPercent = (name, cotizedTotals, plTotal) => {
  const value = name in cotizedTotals ? cotizedTotals[name] : 0
  return (value / plTotal) * 100
}

const buildCotizedByGroup = (walletAnalysis, groupBy) => {
  const posicao = walletAnalysis && walletAnalysis.posicao_completa
  const grupos =
    posicao && Array.isArray(posicao.grupos) ? posicao.grupos : []
  if (!groupBy || !grupos.length) return null
  const zeroed = isPosicaoZeroed(walletAnalysis)
  const totals = {}
  grupos.forEach((grupo) => {
    const ativos = grupo && Array.isArray(grupo.ativos) ? grupo.ativos : []
    ativos.forEach((ativo) => {
      const key = resolveAssetGroupKey(ativo, groupBy, null)
      if (!key) return
      totals[key] = (totals[key] || 0) + resolveAtivoPl(ativo, zeroed)
    })
  })
  return totals
}

const pickAllocationFracoes = (suitability, expanded) => {
  if (!suitability) return {}
  if (expanded && suitability.fracoes_expanded) {
    return suitability.fracoes_expanded
  }
  return suitability.fracoes || {}
}

const findCdiBenchmark = (benchmarks) =>
  (Array.isArray(benchmarks) ? benchmarks : []).find(
    (b) => b && b.nome === 'CDI'
  )

const resolveBenchInfo = ({ benchSelected, mapper, classe, allBenchmarks }) => {
  const cdiBenchmark = findCdiBenchmark(allBenchmarks)
  const bench = benchSelected || mapper[classe] || cdiBenchmark || {}
  const benchmark = allBenchmarks.find(
    (b) => b && b.id === (bench.id_benchmark || bench.id)
  )
  const benchName =
    (benchmark && benchmark.nome) || bench.benchmark || 'Benchmark'
  return { bench, benchName }
}

const computeVolDiff = ({ classeStats, groupBench, bench }) => {
  const benchVol = groupBench[bench.id_benchmark]
    ? groupBench[bench.id_benchmark].vol
    : 0
  const vol = classeStats.vol || 0
  return vol - benchVol
}

const formatBenchmarkLabel = ({ name, diff, formattedDiff }) => {
  if (!name) return formattedDiff
  const wrappedName = name.includes(' + ') ? `(${name})` : name
  const separator = diff >= 0 ? ' + ' : ' '
  return `${wrappedName}${separator}${formattedDiff}`
}
